import { sdk } from '@/lib/api';
import { FetchError } from '@/lib/api/client';
import { queryKeysFactory } from '@/lib/query-key-factory';
import { HttpTypes } from '@/types';
import {
  QueryKey,
  UseInfiniteQueryOptions,
  UseMutationOptions,
  UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import Cookies from 'js-cookie';

const isPartner = Cookies.get('_partner_id') !== undefined;

const CUSTOM_PAGE_QUERY_KEY = 'custom-page-list' as const;
const CUSTOM_PAGE_PREVIEW_QUERY_KEY = 'custom-page-preview-list' as const;

export const customPageQueryKeys = queryKeysFactory(CUSTOM_PAGE_QUERY_KEY);
export const customPagePreviewQueryKeys = queryKeysFactory(CUSTOM_PAGE_PREVIEW_QUERY_KEY);

export const useCustomPages = (
  query: HttpTypes.FindParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminCustomPageListResponse,
      FetchError,
      HttpTypes.AdminCustomPageListResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: customPageQueryKeys.list(query),
    queryFn: async () =>
      isPartner ? sdk.partner.customPages.list(query) : sdk.admin.customPages.list(query),
    ...options,
  });

  return { ...data, ...rest };
};

export const useCreateCustomPage = (
  options?: UseMutationOptions<
    HttpTypes.IResponse<HttpTypes.AdminCustomPageCreateResponse>,
    FetchError,
    HttpTypes.AdminCreateCustomPage
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      isPartner ? sdk.partner.customPages.create(payload) : sdk.admin.customPages.create(payload),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useDetailCustomPage = (
  id: string,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminCustomPageResponse,
      FetchError,
      HttpTypes.AdminCustomPageResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: customPageQueryKeys.detail(id),
    queryFn: async () =>
      isPartner ? sdk.partner.customPages.detail(id) : sdk.admin.customPages.detail(id),
    ...options,
  });

  return { ...data, ...rest };
};

export const useUpdateCustomPage = (
  options?: UseMutationOptions<
    HttpTypes.IResponse<HttpTypes.AdminCustomPageUpdateResponse>,
    FetchError,
    HttpTypes.AdminUpdateCustomPage
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      isPartner ? sdk.partner.customPages.update(payload) : sdk.admin.customPages.update(payload),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useDeleteCustomPage = (
  options?: UseMutationOptions<
    HttpTypes.IResponse<HttpTypes.AdminCustomPageDeleteResponse>,
    FetchError,
    string[]
  >
) => {
  return useMutation({
    mutationFn: (ids) =>
      isPartner ? sdk.partner.customPages.delete(ids) : sdk.admin.customPages.delete(ids),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useInfiniteCustomPages = (
  query: HttpTypes.FindParams,
  options?: Omit<
    UseInfiniteQueryOptions<
      HttpTypes.AdminCustomPageListInfiniteResponse,
      FetchError,
      HttpTypes.AdminCustomPageListInfiniteResponse,
      HttpTypes.AdminCustomPageListInfiniteResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey' | 'getNextPageParam' | 'initialPageParam'
  >
) => {
  return useInfiniteQuery({
    queryKey: customPagePreviewQueryKeys.list(query),
    queryFn: async ({ pageParam = 1 }) => {
      const result = await (isPartner
        ? sdk.partner.customPages.list({ ...query, page: pageParam as number })
        : sdk.admin.customPages.list({ ...query, page: pageParam as number }));

      return {
        data: result?.data ?? [],
        totalCount: result?.totalCount ?? 0,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const pageSize = lastPage?.data?.length ?? 0;
      const currentPage = allPages?.length ?? 0;

      if (currentPage * pageSize < Number(lastPage?.totalCount ?? 0)) {
        return currentPage + 1;
      }

      return undefined;
    },
    ...options,
  });
};

export const useDuplicateCustomPage = (
  options?: UseMutationOptions<
    HttpTypes.IResponse<{ id: string; title: string }> | string,
    FetchError,
    string
  >
) => {
  return useMutation({
    mutationFn: (title: string) => sdk.admin.customPages.duplicate(title),
    onSuccess(response, variables, context) {
      if (typeof response !== 'string' && response?.data) {
        options?.onSuccess?.(response, variables, context);
      } else {
        options?.onSuccess?.(response, variables, context);
      }
    },
    onError(error, variables, context) {
      options?.onError?.(error as FetchError, variables, context);
    },
    ...options,
  });
};
